import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Star } from 'lucide-react';
import { CallInterface, LeadCard, AppointmentCard } from '../components/mockui';
import { SITE } from '../lib/site';

const lineAnim = (reduce, i) =>
  reduce
    ? {}
    : {
        initial: { y: '110%' },
        animate: { y: 0 },
        transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
      };

const fadeAnim = (reduce, delay) =>
  reduce
    ? {}
    : {
        initial: { opacity: 0, y: 18 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
      };

export const Hero = () => {
  const reduce = useReducedMotion();
  const visualRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: visualRef, offset: ['start start', 'end start'] });
  const parY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -36]);
  const parY2 = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -14]);

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-brand-ice via-brand-iceSoft to-white pt-[72px]" data-testid="hero-section">
      <div className="bg-grid mask-fade-b pointer-events-none absolute inset-0" aria-hidden="true" />
      <div
        className="pointer-events-none absolute -top-32 left-1/2 h-[480px] w-[820px] -translate-x-1/2 rounded-full opacity-70 blur-3xl"
        style={{ background: 'radial-gradient(closest-side, rgba(27,168,224,0.16), transparent)' }}
        aria-hidden="true"
      />

      <div className="container-x relative grid items-center gap-14 py-16 lg:grid-cols-[1.02fr_0.98fr] lg:gap-10 lg:py-24">
        <div>
          <motion.p
            {...fadeAnim(reduce, 0)}
            className="inline-flex items-center gap-2 rounded-full border border-brand-lineBlue bg-white/80 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-brand-skyDeep shadow-card backdrop-blur"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand-honey" aria-hidden="true" />
            24/7 AI Voice Assistant
          </motion.p>

          <h1 className="mt-6 text-4xl font-extrabold leading-[1.12] tracking-tight text-brand-navy sm:text-5xl lg:text-6xl">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(reduce, 0)}>
                Generate more leads
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span className="block" {...lineAnim(reduce, 1)}>
                with an{' '}
                <span className="bg-gradient-to-r from-brand-sky to-brand-skyDeep bg-clip-text text-transparent">
                  AI Assistant
                </span>
              </motion.span>
            </span>
          </h1>

          <motion.p {...fadeAnim(reduce, 0.45)} className="mt-6 max-w-xl text-base leading-relaxed text-brand-body/90 sm:text-lg">
            CapableBee is an AI voice assistant that handles your business calls 24/7, capturing leads, booking
            appointments, and gathering customer feedback automatically, so you never miss an opportunity.
          </motion.p>

          <motion.div {...fadeAnim(reduce, 0.58)} className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-brand-sky px-7 py-3.5 text-sm font-bold text-white shadow-soft transition-all hover:-translate-y-0.5 hover:bg-brand-skyDeep hover:shadow-lift"
            >
              Get Started
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <a
              href="#how-it-works"
              data-testid="hero-cta-secondary"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#how-it-works')?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' });
              }}
              className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-7 py-3.5 text-sm font-bold text-brand-navy shadow-card transition-all hover:-translate-y-0.5 hover:shadow-lift"
            >
              See how it works
            </a>
          </motion.div>

          <motion.div {...fadeAnim(reduce, 0.72)} className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
            <div className="flex items-center">
              {['/brand/avatar-rohan.jpg', '/brand/avatar-david.jpg', '/brand/avatar-sophia.jpg'].map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={`CapableBee customer ${i + 1}`}
                  width="40"
                  height="40"
                  loading="lazy"
                  className={`h-10 w-10 rounded-full border-2 border-white object-cover shadow-card ${i > 0 ? '-ml-3' : ''}`}
                />
              ))}
              <div className="-ml-3 flex h-10 w-10 items-center justify-center rounded-full border-2 border-white bg-brand-navy text-[11px] font-extrabold text-white">
                1K+
              </div>
            </div>
            <div>
              <p className="flex items-center gap-1 text-sm font-bold text-brand-navy">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-brand-honey text-brand-honey" aria-hidden="true" />
                ))}
                <span className="ml-1">Rated 5</span>
              </p>
              <p className="text-xs text-slate-500">by over 500+ users · Join 1100+ members</p>
            </div>
          </motion.div>
        </div>

        <div ref={visualRef} className="relative">
          <motion.div style={{ y: parY }} className="relative mx-auto max-w-[540px]">
            <CallInterface />
          </motion.div>

          <motion.div
            style={{ y: parY2 }}
            className="absolute -right-2 -top-6 hidden w-64 animate-none lg:block"
            aria-hidden="true"
          >
            <div style={{ animation: 'floaty 7s ease-in-out infinite' }}>
              <LeadCard name="Sarah Miller" email="sarah@brightside.io" initials="SM" />
            </div>
          </motion.div>

          <motion.div
            style={{ y: parY2 }}
            className="absolute -bottom-8 -left-4 hidden w-60 lg:block"
            aria-hidden="true"
          >
            <div style={{ animation: 'floaty 8s ease-in-out 0.8s infinite' }}>
              <AppointmentCard title="Thu, 2:30 PM" meta="Appointment confirmed" tone="emerald" />
            </div>
          </motion.div>

          <div
            className="pointer-events-none absolute -bottom-10 right-6 hidden h-40 w-40 rounded-full opacity-60 blur-2xl lg:block"
            style={{ background: 'radial-gradient(closest-side, rgba(245,158,11,0.25), transparent)' }}
            aria-hidden="true"
          />
        </div>
      </div>
    </section>
  );
};
